import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeTreatmentProcess } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface TreatmentProcessFormData {
    student: string;
    formId: string;

    userInitials: string;
}

interface TreatmentProcessFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<TreatmentProcessFormData, TreatmentProcessFormProps>;

const validators: Validators<TreatmentProcessFormData> = {
    userInitials: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
};

const TreatmentProcessForm: React.FunctionComponent<InjectedProps & TreatmentProcessFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & TreatmentProcessFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Treatment Process</h2>
            <div>
                We structure our ABA programs in the following way to ensure the best possible outcomes based on
                training, experience and professional standards. All parts of ABA treatment listed below are equally
                important.
                <br />
                <br />
                <div>
                    <b>1. Information Gathering: </b> The beginning of treatment always begins with information
                    gathering and assessments.{' '}
                    <span className={classNames(styles.underline)}> Please email us all reports or evaluations </span>{' '}
                    you have, such as neuropsychological, speech and language, occupational therapy, and IEP. It would
                    also be helpful if we could speak with any current or prior treatment providers should we have
                    clinical questions,{' '}
                    <span className={classNames(styles.underline)}>
                        so please make sure to complete the attached release of information form.
                    </span>
                    <br />
                    <br />
                </div>
                <div>
                    <b>2. Assessment: </b> The very first meeting will be with the parent(s) alone so that we can gather
                    more detailed information and history towards creating the treatment goals. Next, we will meet with
                    the patient/client to begin to assess strengths, areas for growth and possible treatment goals.
                    <br />
                    <br />
                </div>
                <div>
                    <b>3. Program Development: </b> Once your provider has enough information, he/she will begin to
                    create a program manual for treatment planning, activities scheduling, data collection and progress
                    monitoring. This may also include formal skills assessments. During this time, he/she will also be
                    pairing/bonding with the patient to build the therapeutic alliance and gain instructional control.
                    This usually takes several weeks depending on the number of hours allotted per week.
                    <br />
                    <br />
                </div>
                <div>
                    <b>4. Direct Service </b> - Once the goals and reinforcers are clearly identified, the provider(s)
                    will begin to target increasing adaptive skills and reducing maladaptive skills, as well as any
                    academic, social-emotional, or independent living skills. Direct service hours are usually done in
                    2-4 hour blocks of time to allow for better skill acquisition, and skills are taught across settings
                    and providers to improve generalization. If the child is not in school and we are providing ABA in
                    the home, we ask for parents to budget time for community outings and such.
                    <br />
                    <br />
                </div>
                <div>
                    <b>5. Continuous Progress Monitoring </b> - In addition to direct service, providers are spending
                    many required hours each week updating the program manual, analyzing data, writing reports and
                    collaborating with collateral providers (both in and outside of MPG). These hours are vital to any
                    successful ABA program.
                    <br />
                    <br />
                </div>
            </div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="userInitials"
                validate={validators.userInitials}
                wrapperConfig={{
                    classes: {
                        label: 'fullSize',
                    },
                    label: 'Please initial here to verify you read each section below: ',
                }}
            />
            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<TreatmentProcessFormData, TreatmentProcessFormProps>({
    form: 'treatment-process-form',
    enableReinitialize: true,
    onSubmit: async (data: TreatmentProcessFormData, dispatch: Dispatch<any>, props: TreatmentProcessFormProps) => {
        props.setIsLoader(true);
        await asyncPersistIntakeTreatmentProcess({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<TreatmentProcessFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<TreatmentProcessFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<TreatmentProcessFormData, TreatmentProcessFormProps, string>,
    ) {
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(TreatmentProcessForm);
